import { useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Box, ImageIcon, Loader2, GripVertical } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { MediaItem } from '@/components/FigurineGallery';
import MediaUpload, { UploadedMedia } from './MediaUpload';
import { toast } from 'sonner';

interface AdminMediaManagerProps {
  figurineId: string;
  figurineSlug: string;
  items: MediaItem[];
  onReload: () => void;
}

export default function AdminMediaManager({ figurineId, figurineSlug, items, onReload }: AdminMediaManagerProps) {
  const [removing, setRemoving] = useState<string | null>(null);

  const handleUploaded = async (media: UploadedMedia) => {
    const nextOrder = items.length > 0 ? Math.max(...items.map(i => i.order_index)) + 1 : 0;
    const { error } = await supabase.from('figurine_images').insert({
      figurine_id: figurineId,
      url: media.url,
      type: media.type,
      order_index: nextOrder,
    });
    if (error) toast.error(error.message);
    else { toast.success(media.type === 'model3d' ? 'Model 3D dodany!' : 'Zdjęcie dodane!'); onReload(); }
  };

  const handleRemove = async (item: MediaItem) => {
    setRemoving(item.id);
    // Remove from storage
    const path = item.url.split('/figurine-images/')[1]?.split('?')[0];
    if (path) await supabase.storage.from('figurine-images').remove([path]);
    // Remove from DB
    const { error } = await supabase.from('figurine_images').delete().eq('id', item.id);
    if (error) toast.error(error.message);
    else { toast.success('Usunięto.'); onReload(); }
    setRemoving(null);
  };

  const moveUp = async (item: MediaItem, idx: number) => {
    if (idx === 0) return;
    const prev = items[idx - 1];
    await Promise.all([
      supabase.from('figurine_images').update({ order_index: prev.order_index }).eq('id', item.id),
      supabase.from('figurine_images').update({ order_index: item.order_index }).eq('id', prev.id),
    ]);
    onReload();
  };

  return (
    <div className="space-y-3">
      <div className="text-xs font-bold tracking-widest text-muted-foreground flex items-center gap-2"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        MEDIA ({items.length}/10) · Pierwsze zdjęcie = okładka karty
      </div>

      {/* Media list */}
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <motion.div key={item.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 p-2 rounded-xl border"
              style={{ background: 'oklch(0.16 0.03 280)', borderColor: 'oklch(0.25 0.045 280)' }}>
              {/* Thumbnail */}
              <div className="w-10 h-14 rounded-lg flex-shrink-0 overflow-hidden border"
                style={{ borderColor: 'oklch(0.28 0.05 280)', aspectRatio: '9/16' }}>
                {item.type === 'model3d' ? (
                  <div className="w-full h-full flex items-center justify-center"
                    style={{ background: 'oklch(0.68 0.28 195 / 0.15)' }}>
                    <Box className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                  </div>
                ) : (
                  <img src={item.url} alt="" className="w-full h-full object-cover" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  {item.type === 'model3d'
                    ? <><Box className="w-3 h-3 text-accent" /><span className="text-xs font-bold text-accent" style={{ fontFamily: 'JetBrains Mono, monospace' }}>MODEL 3D</span></>
                    : <><ImageIcon className="w-3 h-3 text-primary" /><span className="text-xs font-bold text-primary" style={{ fontFamily: 'JetBrains Mono, monospace' }}>ZDJĘCIE</span></>
                  }
                  {idx === 0 && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'oklch(0.72 0.24 45 / 0.2)', color: 'var(--chart-4)', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem' }}>OKŁADKA</span>}
                </div>
                <p className="text-xs text-muted-foreground truncate" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  poz. {idx + 1} / {items.length}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                <button onClick={() => moveUp(item, idx)} disabled={idx === 0}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-30"
                  title="Przesuń na górę">
                  <GripVertical className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleRemove(item)} disabled={removing === item.id}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                  {removing === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Upload zone */}
      {items.length < 10 && (
        <MediaUpload
          onUploaded={handleUploaded}
          figurineSlug={figurineSlug}
          disabled={items.length >= 10}
        />
      )}
    </div>
  );
}
